import { Button, makeStyles, shorthands, tokens, Tooltip } from '@fluentui/react-components';
import { Call16Regular, Mail16Regular, Open16Regular, Phone16Regular } from '@fluentui/react-icons';
import type { TableRecord } from '@powerportalspro/core';
import { useRecordContext, useT } from '@powerportalspro/react';
import {
  FileGrid,
  resolveAddressLines,
  resolveAddressPosition,
  resolveAddressQuery,
  useAddressMapService,
  type GridCardRendererProps,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useCallback, useMemo, type ReactElement } from 'react';

// What the detail pages share: which record they show, the band of the brand's colors their headers
// sit on and the facts on it that can be followed, the card their grids draw each contact as, and the
// grid of a record's documents.

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const CARD_STRINGS =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.DetailPages.DetailContactCard';

const FACT_STRINGS =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.DetailPages.DetailHeroFactText';

// The details under a contact's name, each behind its icon; one the contact has no value for is
// left out.
const CONTACT_DETAILS: readonly { column: string; icon: ReactElement }[] = [
  { column: 'emailaddress1', icon: <Mail16Regular /> },
  { column: 'telephone1', icon: <Call16Regular /> },
  { column: 'mobilephone', icon: <Phone16Regular /> },
];

const useStyles = makeStyles({
  // A fact that can be followed: the band's own text, underlined, with its focus ring in the same
  // color — nothing of a Fluent control's to repaint on the band.
  factAction: {
    padding: 0,
    ...shorthands.borderStyle('none'),
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    textAlign: 'start',
    cursor: 'pointer',
    textDecorationLine: 'underline',
    textDecorationThickness: tokens.strokeWidthThin,
    textUnderlineOffset: tokens.spacingVerticalXXS,
    ':hover': { textDecorationThickness: tokens.strokeWidthThick },
    ':focus-visible': {
      outlineWidth: tokens.strokeWidthThick,
      outlineStyle: 'solid',
      outlineColor: 'currentColor',
      outlineOffset: tokens.spacingHorizontalXXS,
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  // The contact card: what goes inside it — the grid draws its border, selector and selected state.
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  cardIdentity: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  cardName: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  cardTitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  cardDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  cardDetail: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    color: tokens.colorNeutralForeground2,
  },
  cardValue: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
});

/** Which record a detail page shows: a fixed one, or the one in the address. */
export interface DetailPageProps {
  /** The record to show. Left out, it is taken from {@link queryParameterName}. */
  id?: string;
  /**
   * The query parameter the record's id is read from. With none in the address, the page creates a
   * record, and puts its id there once it is saved.
   */
  queryParameterName?: string;
}

/** A detail page's props, as its `RecordContext` takes them. */
export function recordContextTarget({ id, queryParameterName }: DetailPageProps) {
  return { ...(id ? { id } : {}), ...(queryParameterName ? { queryParameterName } : {}) };
}

/** A file's bytes in base64, as a file column is sent: in chunks, so a large file doesn't overflow the call stack. */
export function toBase64(data: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < data.length; i += 0x8000) {
    binary += String.fromCharCode(...data.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/** Up to two initials of a name, for a record without a picture. */
export function initials(name: string): string {
  return name
    .split(' ')
    .filter((word) => /\p{L}/u.test(word.charAt(0)))
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

/** What a header's fact can be followed to. */
export type HeroFactKind = 'text' | 'address' | 'website' | 'email';

// A website as a link: https unless it names its scheme, and only http or https, so a stored value
// can't link anywhere else. Blazor's twin is DetailHeroFactText's WebsiteHref.
function websiteHref(text: string): string | undefined {
  const trimmed = text.trim();
  try {
    const url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

// An email address as a mailto link: one address, nothing that would add to the message. Blazor's
// twin is DetailHeroFactText's EmailHref.
function emailHref(text: string): string | undefined {
  const address = text.trim();
  return /^[^\s@?&#,;]+@[^\s@?&#,;]+$/.test(address) ? `mailto:${address}` : undefined;
}

/**
 * A fact in a detail page's header, as text or as what it can be followed to: an address opens the
 * portal's map in a dialog, a website opens in a new window and an email address starts a message
 * in the visitor's mail app. Plain elements in the band's own color, not Fluent controls, so nothing
 * on the band is repainted. One that can't be followed — no `<MapsProvider>`, a website that isn't
 * one — is text. Blazor's twin is DetailHeroFactText.
 */
export function HeroFactText({ text, kind = 'text', mapTitle }: {
  text: string;
  kind?: HeroFactKind | undefined;
  mapTitle?: string | undefined;
}) {
  const styles = useStyles();
  const t = useT();
  const { record, dirtyColumns } = useRecordContext();
  const addressMap = useAddressMapService();

  const query = kind === 'address' && addressMap && record ? resolveAddressQuery(record, 'address1', dirtyColumns) : '';
  if (query && addressMap && record) {
    const label = t(`${FACT_STRINGS}.show-on-map`, [text]);
    // The address and where it is are read as it is clicked: at the record's coordinates, else found
    // with the map provider's search.
    const show = () =>
      addressMap.show({
        title: mapTitle || text,
        lines: resolveAddressLines(record, 'address1', undefined, dirtyColumns),
        query,
        position: resolveAddressPosition(record, 'address1', 'address1_latitude', 'address1_longitude', dirtyColumns) ?? undefined,
      });
    return (
      <Tooltip content={label} relationship="label">
        <button type="button" className={styles.factAction} onClick={show}>
          {text}
        </button>
      </Tooltip>
    );
  }

  const website = kind === 'website' ? websiteHref(text) : undefined;
  if (website) {
    return (
      <Tooltip content={t(`${FACT_STRINGS}.open-website`, [text])} relationship="label">
        <a className={styles.factAction} href={website} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      </Tooltip>
    );
  }

  const email = kind === 'email' ? emailHref(text) : undefined;
  if (email) {
    return (
      <Tooltip content={t(`${FACT_STRINGS}.send-email`, [text])} relationship="label">
        <a className={styles.factAction} href={email}>
          {text}
        </a>
      </Tooltip>
    );
  }

  return <>{text}</>;
}

/**
 * The header band's background: the brand's colors. Nothing on it is a control but Fluent's own
 * badges and the facts that can be followed, plain elements in its own color, so its text takes the
 * on-brand foreground and needs nothing else repainted.
 */
export const heroBand = {
  borderRadius: tokens.borderRadiusXLarge,
  backgroundImage: `linear-gradient(135deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandBackgroundPressed} 100%)`,
  color: tokens.colorNeutralForegroundOnBrand,
  boxShadow: tokens.shadow8,
} as const;

/**
 * A contact as the detail pages' grids show one, through the grid's `cardRenderer`: the photo and
 * name at the top with the job title beneath, the email and phones each behind an icon, and a
 * button that opens the contact. `card.field` renders a column as its table cell would — its
 * formatting, its link and the search's highlighting.
 */
export function DetailContactCard({ card }: { card: GridCardRendererProps }) {
  const styles = useStyles();
  const t = useT();
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        {card.avatar}
        <div className={styles.cardIdentity}>
          <span className={styles.cardName}>{card.field('fullname')}</span>
          <span className={styles.cardTitle}>{card.field('jobtitle')}</span>
        </div>
      </div>

      <div className={styles.cardDetails}>
        {CONTACT_DETAILS.filter(({ column }) => card.displayValue(column)).map(({ column, icon }) => (
          <div key={column} className={styles.cardDetail} data-ppp-tooltip={card.label(column)}>
            {icon}
            <span className={styles.cardValue}>{card.field(column)}</span>
          </div>
        ))}
      </div>

      <div className={styles.cardActions}>
        <Button size="small" appearance="subtle" icon={<Open16Regular />} disabled={!card.canOpen} onClick={card.open}>
          {t(`${CARD_STRINGS}.open`)}
        </Button>
      </div>
    </div>
  );
}

/**
 * A record's documents: the `ppp_file` rows whose lookup in `lookupColumn` points at it, to preview
 * and download. A file dropped on the grid is created with that lookup set, through
 * `onUploadRecordAsync`.
 */
export function RecordDocuments({
  view,
  lookupColumn,
  table,
  recordId,
}: {
  view: ViewDefinition;
  lookupColumn: string;
  table: string;
  recordId: string;
}) {
  const views = useMemo(() => [view], [view]);

  const buildDocument = useCallback(
    async (fileName: string, data: Uint8Array): Promise<TableRecord> =>
      ({
        tableName: 'ppp_file',
        properties: {
          ppp_data: { $type: 'FileValue', fileName, id: EMPTY_GUID, value: toBase64(data) },
          [lookupColumn]: { $type: 6, tableName: table, value: recordId },
        },
      }) satisfies TableRecord,
    [lookupColumn, table, recordId],
  );

  return (
    <FileGrid
      tableName="ppp_file"
      fileColumn="ppp_data"
      maxHeight="520px"
      maxFileSizeBytes={512_000}
      customViewDefinitions={views}
      viewIds={[view.id]}
      defaultViewId={view.id}
      onUploadRecordAsync={buildDocument}
    />
  );
}
